<template>

    <!-- Hero Section -->
    <section id="intro" class="min-h-screen grid place-items-center relative bg-cover bg-center" style="background-image: url('river.jpeg');">
    <div class="absolute inset-0 bg-black opacity-50"></div>
    <div class="absolute inset-0 bg-radial-gradient"></div>
    <div class="text-center max-w-4xl px-6 relative z-10 flex flex-col items-center justify-center h-full">
      <i class="fas fa-tree text-6xl mb-4"></i>
      <!-- <img src="logo.png" alt="Map Image" class="my-6 " height="240px" width="240px"> -->

      <h1 class="text-5xl font-bold mb-4">Protect Presumpscot</h1>
      <p class="text-lg mb-6"><i class="fas fa-map-marker-alt mr-2"></i> A conservation oriented nonprofit in Portland, Maine</p>
      <p class="text-lg mb-4 italic">
        <i class="fas fa-leaf mr-2"></i> 
        Our mission is “To advocate for protecting the environmental health and ecological integrity of the Presumpscot River Preserve, the adjacent section of the Presumpscot River, and maintaining the natural experience of the Presumpscot River Trails network.”
      </p>
      
      <div class="space-x-4 mt-6">
        <a href="#learnmore" class="inline-flex items-center justify-center bg-neutral-300 text-gray-900 px-6 py-3 rounded-md transform hover:scale-105 transition-transform duration-300 ease-in-out">
          <i class="fas fa-info-circle mr-2"></i> Learn More
        </a>
        <a href="#getinvolved" class="inline-flex items-center justify-center bg-blue-200 px-6 py-3 rounded-md transform hover:scale-105 transition-transform duration-300 ease-in-out">
          <i class="fas fa-hand-holding-heart mr-2"></i> Get Involved
        </a>
      </div>
      <p class="text-md mb-6 mt-8">
        Additionally, we advocate for low-impact development and preservation of public and natural resources in spaces of consequence to the Presumpscot Preserve and its trails network.
      </p>

<!-- Board Meeting Section -->
<div v-if="showCalendarInvite" class="bg-white bg-opacity-90 p-6 rounded-lg shadow-lg mt-4 max-w-2xl mx-auto">
  <h2 class="text-3xl font-serif font-bold mb-4 text-gray-800"><i class="fas fa-calendar-alt mr-2 text-gray-600"></i> Hybrid Planning Board Meeting</h2>
  
  <!-- Existing description -->
  <p class="text-md mb-4 text-gray-700 italic leading-relaxed">Join us for the next planning board meeting to discuss the development proposal. The meeting will be held both in person and via Zoom.</p>
  
<!-- New content with elegant bullet points -->
<div class="text-md mb-6 bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-md border-l-4 border-gray-300 text-left">
  <ul class="list-square list-inside space-y-3 text-gray-700 leading-relaxed marker:text-gray-500">
    <li class="font-medium text-gray-800">This is the sole item on the agenda, with discussions commencing promptly at 4:30 PM. Public comment is expected to begin early in the meeting.</li>
    <li>We warmly encourage your in-person attendance.</li>
    <li>Should you need assistance crafting talking points for a public statement, we’re here to support you.</li>
    <li>If transportation or childcare is required to facilitate your in-person participation, please don’t hesitate to contact us for assistance.</li>
  </ul>
</div>

  <!-- Date and Time -->
  <p class="text-md mb-4 flex items-center justify-center text-gray-800">
    <i class="fas fa-calendar-day mr-2 text-gray-600"></i> <strong>Tuesday, March 11th, 2025</strong>
    <span class="mx-3 text-gray-400">|</span>
    <i class="fas fa-clock mr-2 text-gray-600"></i> <strong>4:30 PM</strong>
  </p>

  <!-- Location -->
  <p class="text-md mb-6 text-center">
    <i class="fas fa-map-marker-alt mr-2 text-gray-600"></i>
    <a href="https://maps.app.goo.gl/e7j6sdYDMC3RhkMPA" class="no-underline text-gray-800 hover:text-blue-600 transition-colors duration-200">
      <b>Portland City Hall</b>, Room 24 (Basement Level)
    </a>
  </p>

  <!-- Buttons -->
  <div class="mt-6 flex justify-center space-x-6">
    <a 
      href="https://calendar.google.com/calendar/u/0/r/eventedit?text=Hybrid+Planning+Board+Meeting&dates=20250311T203000Z/20250311T223000Z&details=Join+us+for+the+next+planning+board+meeting+to+discuss+the+development+proposal.&location=Portland+City+Hall,+Room+24+(Basement+Level)&sf=true&output=xml&pli=1"
      target="_blank"
      class="inline-flex items-center justify-center bg-blue-100 text-blue-800 px-5 py-2 rounded-full font-medium hover:bg-blue-200 transform hover:scale-105 transition-all duration-300 ease-in-out shadow-sm"
    >
      <i class="fas fa-calendar-plus mr-2"></i> Add to Calendar
    </a>
    <a 
      href="https://portlandme.portal.civicclerk.com/event/7567/overview"
      target="_blank"
      class="inline-flex items-center justify-center bg-gray-100 text-gray-800 px-5 py-2 rounded-full font-medium hover:bg-gray-200 transform hover:scale-105 transition-all duration-300 ease-in-out shadow-sm"
    >
      <i class="fas fa-info-circle mr-2"></i> View Agenda and Details
    </a>
  </div>
</div>
    </div>
</section>

    <!-- Announcements/Events Section -->
    <section v-if="showUpcomingEvents" id="announcements" class="bg-gray-50 py-8">
      <div class="max-w-6xl mx-auto px-4">
        <h2 class="text-3xl font-semibold text-center mb-6 text-gray-800">
          <i class="fas fa-calendar-alt mr-2"></i> Upcoming Events
        </h2>
        
        <div class="grid md:grid-cols-2 gap-6">
          <!-- Portland Land Bank Meeting -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div class="flex items-start justify-between mb-3">
              <h3 class="text-xl font-semibold text-gray-800">Portland Land Bank Meeting</h3>
              <span class="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Public</span>
            </div>
            
            <div class="space-y-2 mb-3 text-sm text-gray-600">
              <div class="flex items-center">
                <i class="fas fa-calendar w-4 mr-2"></i>
                <span class="font-medium">Wed, Aug 13 • 5:00 PM</span>
              </div>
              <div class="flex items-center">
                <i class="fas fa-map-marker-alt w-4 mr-2"></i>
                <span>212 Canco Rd, Suite A</span>
              </div>
            </div>
            
            <p class="text-sm text-gray-700 leading-relaxed">
              Monthly meeting 2 days before the Hope Ave auction. Show support for Land Bank's conservation efforts.
            </p>
          </div>
          
          <!-- Land Auction -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div class="flex items-start justify-between mb-3">
              <h3 class="text-xl font-semibold text-gray-800">Hope Ave Land Auction</h3>
              <span class="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">Public</span>
            </div>
            
            <div class="space-y-2 mb-3 text-sm text-gray-600">
              <div class="flex items-center">
                <i class="fas fa-calendar w-4 mr-2"></i>
                <span class="font-medium">Fri, Aug 15 • 11:00 AM</span>
              </div>
              <div class="flex items-center">
                <i class="fas fa-map-marker-alt w-4 mr-2"></i>
                <span>1 Hope Avenue (on property)</span>
              </div>
            </div>
            
            <p class="text-sm text-gray-700 leading-relaxed mb-3">
              Public foreclosure auction for the Hope Ave parcel. All welcome to attend.
            </p>
            
            <div class="text-xs text-gray-600 space-y-1">
              <div class="font-medium">Parking:</div>
              <div class="flex space-x-4">
                <a href="https://maps.app.goo.gl/i9H1YAqSvj4Fh88L7" class="text-blue-600 hover:text-blue-800">Property boundary</a>
                <a href="https://maps.app.goo.gl/eXNMN1wcHFqfMBaE6" class="text-blue-600 hover:text-blue-800">Basket Lane trailhead</a>
              </div>
              <div class="mt-2">
                <a href="https://keenanauction.com/auction.cgi?i=5511" class="text-blue-600 hover:text-blue-800 inline-flex items-center">
                  <i class="fas fa-external-link-alt mr-1 text-xs"></i>Full details
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Section 1 -->
    <section id="learnmore" class="min-h-screen flex items-center justify-center bg-stone-200 text-stone-800 py-12">
      <div class="max-w-3xl mx-4 sm:mx-6 lg:mx-auto">
        <div class="text-center">
          <h2 class="text-4xl font-semibold mb-4">
            <i class="fas fa-info-circle mr-2"></i> Learn More
          </h2>
          <p class="text-lg mb-6">
            While the 1 Hope Ave parcel is headed to auction, the most recent development proposal is still considered active by Portland Planning. This most recent proposal was for a 
            <a href="https://selfservice.portlandmaine.gov/EnerGov_Prod/SelfService#/plan/d88a5a32-8416-4189-927f-1a7e6cd6f84b" class="text-blue-500 underline hover:text-blue-700">major site development proposal</a> 
            on this 13 acre lot at 1 Hope Ave and abutting the Presumpscot river preserve with planned duplex and triplex units positioned as 
            <span class="font-bold italic rounded">close as 25ft from the North Loop and Brandy Lane connector trails</span> 
            and 
            <span class="font-bold italic rounded">500ft uphill from the river itself</span>.
          </p>
        </div>
        <div class="text-center">
          <img @click="openModal('map.png')" src="map.png" alt="Map Image" class="mt-6 border border-gray-300 shadow-lg rounded-md cursor-pointer">
          <img @click="openModal('map2.png')" src="map2.png" alt="Map Image 2" class="mt-6 border border-gray-300 shadow-lg rounded-md cursor-pointer">
        </div>
        <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div class="flex flex-wrap justify-center">
          <div 
            v-for="image in images" 
            :key="image" 
            class="w-32 h-32 m-4 border-2 border-gray-300 rounded-md cursor-pointer overflow-hidden bg-cover bg-center"
            @click="openGalleryModal(image)"
            :style="{ backgroundImage: `url(${image})` }"
          ></div>
        </div>
        </div>
        <div class="mt-8">
          <h3 class="text-2xl font-semibold mb-4">Protect Presumpscot is concerned about development on this parcel for the following reasons:</h3>
          <ul class="list-disc list-inside text-lg pl-4 mb-6">
            <li>Heightened risk of a catastrophic landslide due to the likely presence of the Presumpscot Formation. <a href="https://www.pressherald.com/2020/09/16/landslide-blocks-presumpscot-river-in-westbrook/" class="text-blue-500 underline hover:text-blue-700">Read about what caused the recent Westbrook landslide</a></li>
            <li>A recent soil survey identifying all 9 unique soil types, indicative of the Presumpscot Formation, on the parcel as poorly draining and unsuitable for development</li>
            <li>Undersized stormwater retention ponds that would discharge overflow onto the Preserve during major storm events damaging this fragile ecosystem</li>
            <li>Construction erosion damaging the Presumpscot Preservation land and River</li>
            <li>Construction debris blowing onto the Presumpscot River Preserve and into the River</li>
            <li>Deterioration of the Presumpscot Preserve's natural experience</li>
            <li>Loss of wetlands and wooded habitat within city limits</li>
          </ul>
        </div>
        
      </div>
    </section>

    <!-- In The News Section -->
    <section id="news" class="bg-stone-100 py-8">
      <div class="max-w-4xl mx-auto px-4">
        <h2 class="text-3xl font-semibold text-center mb-8 text-gray-800">
          <i class="fas fa-newspaper mr-2"></i> In The News
        </h2>
        
        <div class="space-y-4">
          <!-- July 18th 2025 -->
          <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
              <span class="text-sm font-medium text-gray-600 mb-1 sm:mb-0">July 18th, 2025</span>
              <span class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded w-fit">Press Herald</span>
            </div>
            <p class="text-gray-800 mb-2">
              This PPH article sheds light on the details behind why the 1 Hope Ave parcel is in foreclosure
            </p>
            <a href="https://www.pressherald.com/2025/07/18/2-controversial-housing-projects-in-greater-portland-headed-to-auction/" 
               class="text-blue-600 hover:text-blue-800 text-sm inline-flex items-center">
              <i class="fas fa-external-link-alt mr-1"></i>Read Article
            </a>
          </div>

          <!-- April 15th 2025 -->
          <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
              <span class="text-sm font-medium text-gray-600 mb-1 sm:mb-0">April 15th, 2025</span>
              <span class="text-xs bg-green-100 text-green-700 px-2 py-1 rounded w-fit">OpEd</span>
            </div>
            <p class="text-gray-800 mb-2">
              Protect Presumpscot published this OpEd warning of the Landslide Risks associated with developing the 1 Hope Ave parcel
            </p>
            <a href="https://www.pressherald.com/2025/04/15/portland-should-proceed-with-caution-on-north-deering-housing-proposal-opinion/" 
               class="text-blue-600 hover:text-blue-800 text-sm inline-flex items-center">
              <i class="fas fa-external-link-alt mr-1"></i>Read OpEd
            </a>
          </div>

          <!-- March 12th 2025 -->
          <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
              <span class="text-sm font-medium text-gray-600 mb-1 sm:mb-0">March 12th, 2025</span>
              <span class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded w-fit">Press Herald</span>
            </div>
            <p class="text-gray-800 mb-2">
              This PPH article describes some of Protect Presumpscot's efforts to convince the Portland Planning Board to indefinitely table the most recent development proposal
            </p>
            <a href="https://www.pressherald.com/2025/03/12/planning-board-tables-controversial-north-deering-condo-development/" 
               class="text-blue-600 hover:text-blue-800 text-sm inline-flex items-center">
              <i class="fas fa-external-link-alt mr-1"></i>Read Article
            </a>
          </div>

          <!-- March 10th 2025 -->
          <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
              <span class="text-sm font-medium text-gray-600 mb-1 sm:mb-0">March 10th, 2025</span>
              <span class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded w-fit">Press Herald</span>
            </div>
            <p class="text-gray-800 mb-2">
              In this PPH article a Protect Presumpscot spokesperson highlights the negative impacts that a development proposal on the 1 Hope Ave parcel would have to the Presumpscot River Preserve
            </p>
            <a href="https://www.pressherald.com/2025/03/10/developer-who-owes-money-to-city-seeks-approval-for-north-deering-project/" 
               class="text-blue-600 hover:text-blue-800 text-sm inline-flex items-center">
              <i class="fas fa-external-link-alt mr-1"></i>Read Article
            </a>
          </div>

          <!-- June 20th 2024 -->
          <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
              <span class="text-sm font-medium text-gray-600 mb-1 sm:mb-0">June 20th, 2024</span>
              <span class="text-xs bg-green-100 text-green-700 px-2 py-1 rounded w-fit">OpEd</span>
            </div>
            <p class="text-gray-800 mb-2">
              Protect Presumpscot published this OpEd warning of the deleterious effect a development proposal on the 1 Hope Ave parcel would have to the Presumpscot River Preserve
            </p>
            <a href="https://www.pressherald.com/2024/06/20/opinion-portlands-preserved-lands-deserve-better-protection/" 
               class="text-blue-600 hover:text-blue-800 text-sm inline-flex items-center">
              <i class="fas fa-external-link-alt mr-1"></i>Read OpEd
            </a>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Modal -->
    <div v-if="isModalOpen" @click="closeModal" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center">
      <img :src="modalImage" alt="Enlarged Image" class="max-w-full max-h-full rounded">
    </div>

        <!-- Gallery Modal -->
        <div v-if="isGalleryModalOpen" @click="closeGalleryModal" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center gallery-modal">
      <img :src="galleryModalImage" alt="Enlarged Image" class="max-w-full max-h-full rounded">
    </div>


      <!-- Section 2 -->
      <section id="getinvolved" class="min-h-screen flex items-center justify-center py-12">
      <div class="max-w-3xl mx-4 sm:mx-6 lg:mx-auto">
        <div>
          <h2 class="text-4xl font-semibold mb-4 text-center">
            <i class="fas fa-hand-holding-heart mr-2"></i> We're asking for your help!
          </h2>
          
          <p class="text-lg mb-8 text-center">
            The 1 Hope Ave auction opens the door for land conservation organizations to conserve the parcel, add it to the Presumpscot River Preserve, and ensure none of Protect Presumpscot's concerns are realized. However, if another developer is able to purchase the lot, the preserve will be at risk again.
          </p>
          
          <p class="text-xl font-semibold mb-6 text-center">That's why we're asking for your help!</p>
          
          <div class="mt-8 mb-6">
            <h3 class="text-2xl font-semibold mb-4 text-gray-800">Please donate!</h3>
            <ul class="list-disc list-inside text-lg pl-4 mb-6">
              <li class="flex items-center mb-2">
                <i class="fas fa-donate text-green-500 mr-2"></i>
                Visit our GoFundMe: <a href="https://www.gofundme.com/f/help-ensure-low-impact-development-near-the-preserve?utm_medium=copy_link_all&utm_source=customer&utm_campaign=p_cp%2Bshare-sheet&attribution_id=sl%3A2de5654b-ffb5-4e1f-ab8e-ac9e4c3800b2" class="text-blue-500 underline hover:text-blue-700 ml-1">Protect Presumpscot GoFundMe</a>
              </li>
              <li class="flex items-center mb-2">
                <i class="fas fa-wallet text-blue-500 mr-2"></i>
                Venmo @ProtectPresumpscot
              </li>
              <li class="flex items-center">
                <i class="fab fa-paypal text-blue-600 mr-2"></i>
                PayPal @ProtectPresumpscot
              </li>
            </ul>
          <h4 class="text-xl font-semibold mb-4">All donations will be used to…</h4>
          <ul class="list-disc list-inside text-lg pl-4">
            <li>Raise funds to help purchase this lot for purposes of conservation in conjunction with other publicly available funds like Trust for Public Lands, Portland Land Bank and Land For Maine's Future and partners like Portland Trails.</li>
            <li>Pay professionals to independently evaluate any future development's impact on the Preserve's ecosystem</li>
            <li>Retain legal representation at Portland Planning Board meetings to ensure all ecological concerns are voiced to the Portland Planning Board in a formal and informed manner</li>
          </ul>
        </div>

        <h4 class="text-xl mt-8 mb-1 font-semibold">Do you have an opinion or a question about this development?</h4>
        <p class="text-lg mb-2">You can share your thoughts in the following ways:</p>
        <ol class="list-decimal list-inside space-y-2 text-lg pl-4 mb-6">
          <li>Email the Portland Land Bank (<a href="mailto:landbank@portlandmaine.gov" class="text-blue-500 underline hover:text-blue-700">landbank@portlandmaine.gov</a>) to show support for its efforts to acquire the 1 Hope Ave parcel for conservation.
            <ul class="list-disc list-inside pl-8 mt-1">
              <li>Address emails as "To Members of the Portland Land Bank Commission"</li>
              <li>Reference the 1 Hope Ave parcel</li>
            </ul>
          </li>
          <li>Email Kevin Kraft (<a href="mailto:kkraft@portlandmaine.gov" class="text-blue-500 underline hover:text-blue-700">kkraft@portlandmaine.gov</a>) at Portland Planning encouraging the Planning Department to close out the still active development proposal.
            <ul class="list-disc list-inside pl-8 mt-1">
              <li>Address emails as "Dear Director Kraft" and,</li>
              <li>Reference permit number PL-002454-2023.</li>
            </ul>
          </li>
          <li>Write letters-to-the-editor to local news outlets like Portland Press Herald and Bangor Daily News with your opinion of the development.</li>
          <li>Get in touch with your city councilor to voice any concerns you may have:
            <ul class="list-disc list-inside pl-4">
              <li>D1: Anna Trevorrow (<a href="mailto:atrevorrow@portlandmaine.gov" class="text-blue-500 underline hover:text-blue-700">atrevorrow@portlandmaine.gov</a>)</li>
              <li>D2: Victoria Pelletier (<a href="mailto:vpelletier@portlandmaine.gov" class="text-blue-500 underline hover:text-blue-700">vpelletier@portlandmaine.gov</a>)</li>
              <li>D3: Regina Phillips (<a href="mailto:rphillips@portlandmaine.gov" class="text-blue-500 underline hover:text-blue-700">rphillips@portlandmaine.gov</a>)</li>
              <li>D4: Anna Bullett (<a href="mailto:abullett@portlandmaine.gov" class="text-blue-500 underline hover:text-blue-700">abullett@portlandmaine.gov</a>)</li>
              <li>D5: Kate Sykes (<a href="mailto:ksykes@portlandmaine.gov" class="text-blue-500 underline hover:text-blue-700">ksykes@portlandmaine.gov</a>)</li>
            </ul>
          </li>
        </ol>
        <p class="text-lg mb-4">Looking for more information? Looking to get involved? Email <a href="mailto:ProtectPresumpscot@gmail.com" class="text-blue-500 underline hover:text-blue-700">ProtectPresumpscot@gmail.com</a></p>

        </div>
        
      </div>
    </section>
    <!-- Footer -->
    <footer class="text-center w-full flex flex-col sm:flex-row items-center justify-between bg-stone-200 text-stone-800 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
    <p class="text-lg flex items-center mb-2 sm:mb-0"><i class="fas fa-map-marker-alt mr-2"></i> Protect Presumpscot</p>
    <p class="text-lg sm:ml-6 italic text-center sm:text-left">A conservation oriented nonprofit in Portland, Maine</p>
</footer>



</template>

<script>
export default {
  data() {
    return {
      showCalendarInvite: false,
      showUpcomingEvents: false,
      isModalOpen: false,
      modalImage: '',
      images: [
        'gallery/1.jpeg',
        'gallery/2.jpeg',
        'gallery/3.jpeg'
      ],
      isGalleryModalOpen: false,
      galleryModalImage: ''
    };
  },
  methods: {
    openModal(src) {
      this.modalImage = src;
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
      this.modalImage = '';
    },
    openGalleryModal(image) {
      this.galleryModalImage = image;
      this.isGalleryModalOpen = true;
    },
    closeGalleryModal() {
      this.isGalleryModalOpen = false;
      this.galleryModalImage = '';
    }
  }
};
</script>



<style scoped>
.modal {
  display: none;
}
.modal.active {
  display: flex;
}

.bg-radial-gradient {
        background: radial-gradient(circle, rgb(255, 255, 255), rgba(255, 255, 255, 0.49) 100%);
    }
</style>

